import type { Dayjs } from 'dayjs';
import dayjs from '~/timeInit';

/**
 * A month, as `YYYY-MM`.
 *
 * Partner invoicing bills whole calendar months, so both the transactions view and the invoice
 * select a month rather than a free start/end range. Keeping the selection as a plain string (not
 * a `Dayjs`) makes it a stable React Query key and keeps the month out of timezone arithmetic —
 * a month has no instant, so it cannot drift across a UTC/Sydney boundary the way a date can.
 */
export type InvoiceMonth = string;

/** `dayjs` format for {@link InvoiceMonth}. */
export const MONTH_FORMAT = 'YYYY-MM';

/** `dayjs` format for the plain `YYYY-MM-DD` Sydney days the invoicing endpoints accept. */
export const DAY_FORMAT = 'YYYY-MM-DD';

/** An inclusive `start`/`end` day range, exactly as the invoicing endpoints expect it. */
export interface MonthRange {
  start: string;
  end: string;
}

/** Parses an {@link InvoiceMonth} into a `Dayjs` at the first instant of that month. */
function parseMonth(month: InvoiceMonth): Dayjs {
  return dayjs(month, MONTH_FORMAT);
}

/**
 * Expands a month into the inclusive day range the invoicing endpoints take.
 *
 * @param month - The month, as `YYYY-MM`.
 * @returns The month's first and last calendar day, each as `YYYY-MM-DD`.
 */
export function monthToRange(month: InvoiceMonth): MonthRange {
  const start = parseMonth(month);
  return {
    start: start.startOf('month').format(DAY_FORMAT),
    end: start.endOf('month').format(DAY_FORMAT),
  };
}

/**
 * Formats a month for display, e.g. for the invoice's
 * `"Société points issued for August 2026"` line-item description.
 */
export function formatMonthLabel(month: InvoiceMonth): string {
  return parseMonth(month).format('MMMM YYYY');
}

/** Reduces any `YYYY-MM-DD` day (or `Dayjs`) to the month containing it. */
export function toMonth(date: string | Dayjs): InvoiceMonth {
  return dayjs(date).format(MONTH_FORMAT);
}

/** Steps a month forward (positive) or back (negative) by whole months. */
export function shiftMonth(month: InvoiceMonth, months: number): InvoiceMonth {
  return parseMonth(month).add(months, 'month').format(MONTH_FORMAT);
}

/**
 * Whether `month` falls after `reference`.
 *
 * Compared as dates rather than strings so a year rollover (`2027-01` vs `2026-12`) orders
 * correctly — which a naive string comparison also happens to get right for zero-padded `YYYY-MM`,
 * but only by coincidence of the format, and this must not silently break if the format changes.
 */
export function isMonthAfter(month: InvoiceMonth, reference: InvoiceMonth): boolean {
  return parseMonth(month).isAfter(parseMonth(reference), 'month');
}

/**
 * The month it currently is in the program's timezone (Australia/Sydney by default).
 *
 * Uses the configured default timezone rather than the browser's, because the service prices
 * activity by Sydney day — an admin in another timezone must not see a different "this month".
 */
export function currentMonth(): InvoiceMonth {
  return dayjs().tz().format(MONTH_FORMAT);
}

/**
 * Today, in the program's timezone, as `YYYY-MM-DD`.
 *
 * Uses the configured default timezone rather than the browser's, for the same reason
 * {@link currentMonth} does: the service prices activity by Sydney day, so an admin elsewhere must
 * not see a different "today".
 */
export function currentDay(): string {
  return dayjs().tz().format(DAY_FORMAT);
}

/**
 * The first day of the month after `reference` (default: today), as `YYYY-MM-DD`.
 *
 * The default due date for a partner invoice — issued during or just after a period, payable at
 * the start of the following month.
 */
export function firstDayOfNextMonth(reference: string = currentDay()): string {
  return dayjs(reference).add(1, 'month').startOf('month').format(DAY_FORMAT);
}

/**
 * Drops days that have not happened yet in the program's timezone.
 *
 * A period covering the running month comes back with a row for every calendar day, so an invoice
 * viewed mid-month lists a tail of future dates that can only ever read zero. They are noise on the
 * document and confusing on a printed invoice. Dropping them is display-only: the invoice's totals
 * come from the service and future days contribute nothing to them.
 *
 * @param days - The invoice's day rows.
 * @param today - Override for "today", for testing.
 */
export function withoutFutureDays<T extends { date: string }>(days: Array<T>, today: string = currentDay()): Array<T> {
  return days.filter((day) => day.date <= today);
}
